import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { api, setTokens, clearTokens, setOnAuthExpired, tryRestoreSession, type TokenResponse } from "@/lib/api";

interface User {
  id: string;
  email: string;
  displayName: string | null;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true); // true until bootstrap completes

  const fetchUser = useCallback(async () => {
    const me = await api.auth.me();
    setUser({
      id: me.id,
      email: me.email,
      displayName: me.display_name,
    });
  }, []);

  const handleAuthResponse = useCallback(async (data: TokenResponse) => {
    setTokens(data.access_token, data.refresh_token);
    await fetchUser();
  }, [fetchUser]);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  // ── Bootstrap: try to restore session from persisted refresh token ──

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const restored = await tryRestoreSession();
        if (restored && !cancelled) {
          await fetchUser();
        }
      } catch {
        // Refresh token was invalid or network error — stay logged out
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, [fetchUser]);

  useEffect(() => {
    setOnAuthExpired(logout);
  }, [logout]);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const data = await api.auth.login(email, password);
      await handleAuthResponse(data);
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthResponse]);

  const register = useCallback(async (email: string, password: string, displayName?: string) => {
    setIsLoading(true);
    try {
      const data = await api.auth.register(email, password, displayName);
      await handleAuthResponse(data);
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthResponse]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
